import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate /* , Link */ } from 'react-router-dom';
import authService, { LoginCredentials } from '../services/AuthService';
import authErrorHandler from '../services/AuthErrorHandler';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import { HttpError } from '../../../core/services/HttpService';

function Login(): React.JSX.Element {
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [statusMsg, setStatusMsg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const navigate = useNavigate();

  // Check for stored error messages on component mount
  useEffect(() => {
    const storedError = authService.getAuthError();
    if (storedError) {
      setError(storedError);
      // Clear the error from storage after displaying it
      authService.clearAuthError();
    }
  }, []);

  const handleLogin = async (): Promise<void> => {
    // Reset error and set loading state
    setError('');
    setStatusMsg('');
    setIsLoading(true);

    try {
      const credentials: LoginCredentials = {
        username: email,
        password: password
      };

      await authService.login(credentials);

      setStatusMsg('Erfolgreich eingeloggt. Leite weiter...');

      // Force a small delay to ensure the tokens are stored
      setTimeout(() => {
        // Use window.location for a complete page refresh to ensure auth state is updated
        window.location.href = '/home';
      }, 500);

    } catch (error) {
      if (error instanceof HttpError) {
        // Try auth-specific error handling first
        const authHandled = authErrorHandler.handleLoginError(error);
        if (!authHandled) {
          // Try global error handling
          const globalHandled = globalErrorHandler.handle(error);
          if (!globalHandled) {
            // Fallback for unhandled errors
            const detail = error.data?.detail || 'Ein unbekannter Fehler ist aufgetreten';
            setError(`Login fehlgeschlagen: ${detail}`);
          }
        } else {
          // Auth handler dealt with the error, but we might still want to show it in the component
          const detail = error.data?.detail || 'Login fehlgeschlagen';
          setError(detail);
        }
      } else {
        // Non-HTTP error
        globalErrorHandler.handleUnexpected(error);
        setError('Ein unerwarteter Fehler ist aufgetreten');
      }
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPassword(e.target.value);
  };

  return (
    <div style={styles.container}>
      {/* Left: Brand Panel */}
      <div style={styles.brandPanel}>
        <div style={styles.brandContent}>
          <h1 style={styles.brandTitle}>SundaeOS</h1>
          <div style={styles.brandDivider} />
          <p style={styles.brandTagline}>
            Das Managementsystem<br />
            für deinen Schichtbetrieb
          </p>
        </div>
      </div>

      {/* Right: Form Panel */}
      <div style={styles.formPanel}>
        <div style={styles.formWrapper}>
          <h2 style={styles.formTitle}>Willkommen</h2>
          <p style={styles.formSubtitle}>Meld dich an, um loszulegen</p>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Email / Username</label>
            <input
              type="email"
              value={email}
              onChange={handleEmailChange}
              style={styles.input}
              placeholder="Gib deine Email ein"
              onKeyDown={handleKeyDown}
            />
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Passwort</label>
            <input
              type="password"
              value={password}
              onChange={handlePasswordChange}
              style={styles.input}
              placeholder="Gib dein Passwort ein"
              onKeyDown={handleKeyDown}
            />
          </div>

          {error && (
            <div style={styles.errorMessage}>
              {error}
            </div>
          )}

          {statusMsg && (
            <div style={styles.successMessage}>
              {statusMsg}
            </div>
          )}

          <button
            onClick={handleLogin}
            style={isLoading ? {...styles.loginButton, ...styles.loginButtonDisabled} : styles.loginButton}
            disabled={isLoading}
          >
            {isLoading ? 'Anmeldung...' : 'Anmelden'}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    minHeight: '100vh',
  },

  /* ---- Left Brand Panel ---- */
  brandPanel: {
    flex: '0 0 45%',
    background: 'linear-gradient(160deg, #0E1333 0%, #1a2340 40%, #E7007D 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px',
    position: 'relative',
    overflow: 'hidden',
  },
  brandContent: {
    position: 'relative',
    zIndex: 1,
  },
  brandTitle: {
    margin: 0,
    fontSize: '48px',
    color: '#ffffff',
    fontWeight: '700',
    letterSpacing: '1px',
  },
  brandDivider: {
    width: '60px',
    height: '3px',
    background: 'rgba(255,255,255,0.5)',
    margin: '24px 0',
    borderRadius: '2px',
  },
  brandTagline: {
    margin: 0,
    fontSize: '20px',
    color: 'rgba(255,255,255,0.8)',
    fontWeight: '400',
    lineHeight: '1.6',
  },

  /* ---- Right Form Panel ---- */
  formPanel: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#f8fafc',
    padding: '60px 40px',
  },
  formWrapper: {
    width: '100%',
    maxWidth: '380px',
  },
  formTitle: {
    margin: '0 0 6px 0',
    fontSize: '28px',
    fontWeight: '600',
    color: '#0E1333',
  },
  formSubtitle: {
    margin: '0 0 36px 0',
    fontSize: '15px',
    color: '#64748b',
  },
  inputGroup: {
    marginBottom: '22px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '500',
    color: '#334155',
  },
  input: {
    width: '100%',
    padding: '12px 15px',
    fontSize: '15px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    outline: 'none',
    backgroundColor: '#ffffff',
    color: '#0E1333',
  },
  loginButton: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(90deg, #0E1333 0%, #0E1333 30%, #E7007D 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '50px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
    marginTop: '12px',
  },
  loginButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  errorMessage: {
    color: '#ef4444',
    padding: '12px 16px',
    backgroundColor: '#fee2e2',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    textAlign: 'center',
    border: '1px solid #fecaca',
    fontWeight: '500',
  },
  successMessage: {
    color: '#22c55e',
    padding: '12px 16px',
    backgroundColor: '#dcfce7',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '14px',
    textAlign: 'center',
    border: '1px solid #bbf7d0',
  },
};

export default Login;
